import { useEffect, useState } from 'react';
import { useDeliveryMutations } from '../../hooks/useDelivery';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SendQrModalProps {
  open: boolean;
  guestId: number | null;
  participantName: string;
  storedEmail: string;
  initialEmail?: string;
  onClose: () => void;
  onSent?: (message: string) => void;
}

export function SendQrModal({
  open,
  guestId,
  participantName,
  storedEmail,
  initialEmail,
  onClose,
  onSent,
}: SendQrModalProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const { resend } = useDeliveryMutations();

  useEffect(() => {
    if (open) {
      setEmail(initialEmail ?? storedEmail ?? '');
      setError('');
    }
  }, [open, initialEmail, storedEmail, guestId]);

  if (!open) return null;

  async function handleSend() {
    if (guestId == null) return;
    const value = email.trim();

    if (!value && !storedEmail) {
      setError('Укажите адрес email');
      return;
    }
    if (value && !EMAIL_REGEX.test(value)) {
      setError('Неверный формат email');
      return;
    }

    setError('');
    try {
      const res = await resend.mutateAsync({ guestId, email: value || undefined });
      const destination = res.sent_to || storedEmail;
      if (res.success) {
        onSent?.(`QR-код отправлен: ${participantName} → ${destination}`);
      } else {
        onSent?.(`Не удалось отправить ${participantName}: ${res.error ?? 'неизвестная ошибка'}`);
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Ошибка отправки');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-xl bg-ct-surface border border-ct-border shadow-ct-md p-6">
        <h3 className="text-lg font-bold text-ct-text">Отправить QR-код</h3>
        <p className="mt-1 text-sm text-ct-text-muted">{participantName}</p>

        <label className="mt-4 block text-sm font-semibold text-ct-text">
          Адрес email
          <span className="ml-1 font-normal text-ct-text-muted">(необязательно)</span>
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={storedEmail || 'email@example.com'}
          autoFocus
          className="mt-1 w-full rounded-lg border border-ct-border bg-ct-surface px-3 py-2 text-sm text-ct-text shadow-ct-sm focus:outline-none focus:ring-2 focus:ring-ct-primary/30"
        />
        <p className="mt-1 text-xs text-ct-text-muted">
          {storedEmail
            ? `Основной адрес участника: ${storedEmail}. Оставьте поле как есть или укажите другой адрес — основной email не изменится.`
            : 'У участника нет email — укажите адрес для отправки.'}
        </p>

        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={resend.isPending}
            className="rounded-lg border border-ct-border bg-ct-surface px-5 py-2 text-sm font-semibold text-ct-text-muted transition-all duration-200 hover:bg-indigo-50 hover:text-ct-primary active:scale-[0.98] disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={handleSend}
            disabled={resend.isPending}
            className="rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary px-5 py-2 text-sm font-bold text-white shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98] disabled:opacity-50"
          >
            {resend.isPending ? 'Отправка…' : 'Отправить'}
          </button>
        </div>
      </div>
    </div>
  );
}
